import { useEffect, useMemo, useState } from "react";
import { usePuck, type ComponentData } from "@measured/puck";
import { useGetAttachmentById, type Attachment } from "@/api/attachment";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import {
  attachmentMeasureUrl,
  attachmentStoredPixelSize,
  editRecipeChangesSize,
  editedPixelSize,
  parseEditRecipe,
  puckMediaSizeFieldOptions,
} from "@/lib/cms-puck/media-utils";
import type { MediaSize } from "@/components/cms-puck/fields/MediaPickerField";

/**
 * Dimensions EFFECTIVES de l'image du bloc, retouche comprise (crop, rotation…).
 * Les dimensions SOURCE viennent de `details` (ou d'une mesure) ; si l'image a été
 * retouchée (`editRecipe`), on applique la recette → le select « Taille » reflète
 * la vraie taille, pas celle d'avant recadrage (évite les incohérences).
 */
function useNaturalPixelSize(att: Attachment | undefined | null) {
  const stored = attachmentStoredPixelSize(att);
  const recipe = useMemo(
    () => parseEditRecipe(att?.editRecipe),
    [att?.editRecipe],
  );
  const retouched = editRecipeChangesSize(recipe);

  const [measured, setMeasured] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Sans dimensions SOURCE stockées, on mesure. Si retouché, on mesure l'image
  // DÉJÀ retouchée (large) → ne pas réappliquer la recette ensuite.
  const measureUrl = stored
    ? undefined
    : retouched
      ? (att?.largePublicUrl ?? attachmentMeasureUrl(att))
      : attachmentMeasureUrl(att);

  useEffect(() => {
    if (stored || !measureUrl) {
      setMeasured(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setMeasured({ width: img.naturalWidth, height: img.naturalHeight });
      }
    };
    img.onerror = () => {
      if (!cancelled) setMeasured(null);
    };
    img.src = measureUrl;
    return () => {
      cancelled = true;
    };
  }, [stored, measureUrl]);

  // Le crop/rotation s'applique aux dimensions SOURCE (`details`). Si on a dû
  // MESURER (pas de `details`), l'URL mesurée reflète déjà la retouche → on la
  // prend telle quelle sans réappliquer la recette.
  if (stored) return editedPixelSize(stored, recipe);
  return measured;
}

/**
 * Select « Taille (image) » : libellés avec dimensions réelles après fit:inside
 * du convertisseur, selon le ratio portrait/paysage de l'image du bloc.
 */
export function MediaSizeField({
  value,
  onChange,
}: {
  value: MediaSize | string | undefined;
  onChange: (value: MediaSize) => void;
}) {
  const cmsNew = useCmsNew();
  const { selectedItem } = usePuck();
  const item = selectedItem as ComponentData | null;
  const attachmentId =
    typeof item?.props?.attachmentId === "string"
      ? item.props.attachmentId
      : "";

  const { data } = useGetAttachmentById(attachmentId);
  const att = attachmentId ? (data as Attachment | undefined) : undefined;
  const natural = useNaturalPixelSize(att);

  const options = useMemo(
    () =>
      puckMediaSizeFieldOptions(cmsNew, {
        naturalWidth: natural?.width,
        naturalHeight: natural?.height,
      }),
    [cmsNew, natural?.width, natural?.height],
  );

  const current = value && options.some((o) => o.value === value) ? value : "large";

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {cmsNew("cms.new.auto.label.taille_image", "Taille (image)")}
      </Label>
      <Select
        value={current}
        onValueChange={(next) => onChange(next as MediaSize)}
      >
        <SelectTrigger className="h-9 w-full bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
